<template>
  <div class="lms-index-table" :class="{ 'is-embedded': embedded }">
    <div class="lms-index-table__wrap">
      <v-data-table-server
        :headers="headers"
        :items="rows"
        :items-length="filtered"
        :loading="loading"
        :page="page"
        :items-per-page="itemsPerPage"
        :items-per-page-options="pageOptions"
        :sort-by="sortBy"
        item-value="id"
        hover
        density="comfortable"
        class="lms-index-table__dt"
        @update:page="$emit('update:page', $event)"
        @update:items-per-page="$emit('update:itemsPerPage', $event)"
        @update:sort-by="$emit('update:sortBy', $event)"
        @click:row="onClickRow"
      >
        <template v-if="showColumnFilters" #headers="headerProps">
          <IndexColumnFilters
            v-bind="headerProps"
            :model-value="columnFilters"
            :meta-columns="metaColumns"
            :exclude="['_select', '_actions']"
            :debounce-ms="320"
            :select-all="selectionEnabled ? onHeaderSelectAll : null"
            :all-selected="allFilteredSelected"
            :some-selected="someFilteredSelected"
            @update:model-value="onColumnFiltersUpdate"
            @apply="$emit('apply-column-filters')"
          />
        </template>

        <template v-else-if="selectionEnabled" #header._select>
          <div class="lms-index-table__select-head" @click.stop>
            <v-checkbox-btn
              :model-value="allFilteredSelected"
              :indeterminate="someFilteredSelected && !allFilteredSelected"
              color="primary"
              @update:model-value="onHeaderSelectAll"
            />
          </div>
        </template>

        <template v-if="selectionEnabled" #item._select="{ item }">
          <v-checkbox
            :model-value="isSelected(rawOf(item).id)"
            density="compact"
            hide-details
            @click.stop
            @update:model-value="$emit('toggle-selected', rawOf(item).id)"
          />
        </template>

        <template v-for="col in visibleColumns" :key="col.key" #[`item.${col.key}`]="{ item }">
          <div class="lms-index-table__cell" :class="{ 'is-primary': isPrimaryCol(col) }">
            <div v-if="isPrimaryCol(col)" class="lms-index-table__primary">
              <div
                v-if="signalsOf(item).length"
                class="lms-index-table__signals"
              >
                <span
                  v-for="sig in signalsOf(item)"
                  :key="sig.id + sig.label"
                  class="lms-index-table__signal"
                  :class="`is-${sig.tone || 'info'}`"
                  :title="sig.label"
                >
                  <span class="lms-index-table__signal-dot" />
                  {{ sig.label }}
                </span>
              </div>
              <a
                v-if="cellHref(rawOf(item), col)"
                class="lms-index-table__title-link"
                :class="{ 'lms-index-table__title-link--wrapped': isWrappedCol(col) }"
                :href="cellHref(rawOf(item), col)"
                :target="linkTarget || undefined"
                :rel="linkTarget === '_blank' ? 'noopener' : undefined"
                @click.stop
              >
                {{ displayCell(rawOf(item), col) }}
              </a>
              <span
                v-else
                class="lms-index-table__title-link"
                :class="{ 'lms-index-table__title-link--wrapped': isWrappedCol(col) }"
              >
                {{ displayCell(rawOf(item), col) }}
              </span>
            </div>
            <template v-else-if="col.type === 'color'">
              <span class="lms-index-table__swatch" :style="{ background: rawOf(item)[col.key] }" />
            </template>
            <template v-else-if="col.type === 'html'">
              <div class="lms-index-table__html" v-html="rawOf(item)[col.key]" @click.stop />
            </template>
            <template v-else-if="col.type === 'link' && rawOf(item)[col.key]?.if">
              <a
                class="lms-index-table__link-btn"
                :href="rawOf(item)[col.key].link"
                :target="linkTarget || undefined"
                :rel="linkTarget === '_blank' ? 'noopener' : undefined"
                @click.stop
              >
                <v-icon :icon="mdiFromFa(col.icon)" size="16" />
                Open
              </a>
            </template>
            <template v-else-if="cellHref(rawOf(item), col)">
              <a
                class="lms-index-table__title-link"
                :class="{ 'lms-index-table__title-link--wrapped': isWrappedCol(col) }"
                :href="cellHref(rawOf(item), col)"
                :target="linkTarget || undefined"
                :rel="linkTarget === '_blank' ? 'noopener' : undefined"
                @click.stop
              >
                {{ displayCell(rawOf(item), col) }}
              </a>
            </template>
            <span
              v-else
              :class="{ 'lms-index-table__clamp': col.tdClass?.includes('wrapped') }"
            >
              {{ displayCell(rawOf(item), col) }}
            </span>
          </div>
        </template>

        <template #item._actions="{ item }">
          <div class="lms-index-table__actions" @click.stop>
            <button
              v-for="(action, i) in rowActionsOf(item)"
              :key="'ra'+i"
              type="button"
              class="lms-index-table__action"
              :class="actionToneClass(action.tone)"
              @click="$emit('row-action', action, rawOf(item))"
            >
              {{ action.label }}
            </button>
            <a
              v-if="rowHref(rawOf(item))"
              class="lms-index-table__action lms-index-table__action--edit"
              :href="rowHref(rawOf(item))"
            >
              Open
            </a>
            <button
              v-if="peekEnabled"
              type="button"
              class="lms-index-table__action lms-index-table__action--peek"
              @click="$emit('peek', rawOf(item))"
            >
              Peek
            </button>
          </div>
        </template>

        <template #no-data>
          <div class="py-10 px-4">
            <LmsEmptyState
              :title="emptyTitle"
              :message="emptyMessage"
              icon="mdi-table-search"
            >
              <v-btn
                v-if="hasActiveFilters"
                color="primary"
                variant="tonal"
                size="small"
                prepend-icon="mdi-filter-off-outline"
                @click="$emit('clear-filters')"
              >
                Clear filters
              </v-btn>
            </LmsEmptyState>
          </div>
        </template>

        <template #loading>
          <div class="lms-index-table__loading">
            <v-progress-circular indeterminate color="primary" size="28" />
            <span>Loading…</span>
          </div>
        </template>

        <template #bottom>
          <div class="lms-index-table__footer">
            <div class="lms-index-table__footer-meta">
              <span
                v-if="paginationSummary"
                class="lms-index-table__footer-summary"
              >
                {{ paginationSummary }}
              </span>
            </div>
            <v-pagination
              :model-value="page"
              :length="pageCount"
              density="comfortable"
              active-color="primary"
              :total-visible="Math.min(pageCount, 7)"
              show-first-last-page
              @update:model-value="$emit('update:page', $event)"
            />
          </div>
        </template>
      </v-data-table-server>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import IndexColumnFilters from '@/shared/components/IndexColumnFilters.vue'
import { formatIndexPaginationSummary } from '@/shared/composables/useIndexPaginationSummary'
import { exportXlsx } from '@/shared/utils/exportXlsx'
import { showCopyFeedback } from '@/nov/composables/useNovTableTools'
import LmsEmptyState from './LmsEmptyState.vue'
import { displayCell } from '../utils/indexCellDisplay'

const props = defineProps({
  columns: { type: Array, default: () => [] },
  visibleColumns: { type: Array, default: () => [] },
  rows: { type: Array, default: () => [] },
  loading: { type: Boolean, default: false },
  filtered: { type: Number, default: 0 },
  total: { type: Number, default: 0 },
  page: { type: Number, default: 1 },
  itemsPerPage: { type: Number, default: 10 },
  sortBy: { type: Array, default: () => [] },
  pluralTitle: { type: String, default: 'records' },
  rowHref: { type: Function, default: () => null },
  hasActiveFilters: { type: Boolean, default: false },
  embedded: { type: Boolean, default: false },
  peekEnabled: { type: Boolean, default: true },
  selectionEnabled: { type: Boolean, default: false },
  selectedIds: { type: Array, default: () => [] },
  allFilteredSelected: { type: Boolean, default: false },
  someFilteredSelected: { type: Boolean, default: false },
  columnFilters: { type: Object, default: () => ({}) },
  showColumnFilters: { type: Boolean, default: false },
  linkTarget: { type: String, default: null },
  /** Optional async () => rows[] for full filtered export (classic parity). */
  fetchExportRows: { type: Function, default: null }
})

const emit = defineEmits([
  'update:page',
  'update:itemsPerPage',
  'update:sortBy',
  'peek',
  'open-row',
  'clear-filters',
  'toggle-selected',
  'select-all',
  'set-column-filters',
  'apply-column-filters',
  'row-action'
])

const metaColumns = computed(() =>
  (props.columns || []).map((c) => ({
    name: c.key,
    no_search: c.searchable === false,
    type: c.type || null
  }))
)

function onColumnFiltersUpdate (next) {
  emit('set-column-filters', next || {})
}

function onHeaderSelectAll (checked) {
  emit('select-all', !!checked)
}

const emptyTitle = computed(() =>
  props.hasActiveFilters ? 'No matching results' : `No ${props.pluralTitle}`
)
const emptyMessage = computed(() =>
  props.hasActiveFilters
    ? 'Try adjusting search or filters to find what you need.'
    : `There are no ${String(props.pluralTitle).toLowerCase()} to show yet.`
)

const pageOptions = [
  { value: 10, title: '10' },
  { value: 25, title: '25' },
  { value: 50, title: '50' },
  { value: 100, title: '100' },
  { value: -1, title: 'All' }
]

const pageCount = computed(() => {
  if (props.itemsPerPage === -1) return 1
  const per = Number(props.itemsPerPage) || 25
  return Math.max(1, Math.ceil((Number(props.filtered) || 0) / per))
})

const paginationSummary = computed(() =>
  formatIndexPaginationSummary({
    page: props.page,
    itemsPerPage: props.itemsPerPage,
    filtered: props.filtered,
    total: props.total,
    noun: 'entries'
  })
)

const headers = computed(() => {
  const cols = []
  if (props.selectionEnabled) {
    cols.push({
      title: '',
      key: '_select',
      sortable: false,
      width: '48px'
    })
  }
  const hasRowActions = props.rows.some((r) => Array.isArray(rawOf(r)._rowActions) && rawOf(r)._rowActions.length)
  if (props.peekEnabled || props.rows.some((r) => props.rowHref(rawOf(r))) || hasRowActions) {
    cols.push({
      title: 'Actions',
      key: '_actions',
      sortable: false,
      align: 'start',
      width: '200px'
    })
  }
  props.visibleColumns.forEach((c) => {
    const isId = c.key === 'id' || c.title?.toLowerCase() === 'id'
    const endAlign = !isId && (c.type === 'money' || c.type === 'number')
    cols.push({
      title: c.title,
      key: c.key,
      sortable: c.sortable !== false,
      align: endAlign ? 'end' : 'start',
      width: isId ? '72px' : undefined,
      minWidth: isId ? '64px' : undefined
    })
  })
  return cols
})

function mdiFromFa (fa) {
  if (!fa) return 'mdi-open-in-new'
  if (fa.includes('pencil')) return 'mdi-pencil'
  if (fa.includes('eye')) return 'mdi-eye'
  return 'mdi-open-in-new'
}

function rawOf (item) {
  return item?.raw ?? item ?? {}
}

function signalsOf (item) {
  const raw = rawOf(item)
  return Array.isArray(raw._signals) ? raw._signals : []
}

function cellHref (row, col) {
  if (!row || !col?.key) return null
  const keyed = row[`_link_${col.key}`]
  if (keyed) return keyed
  if (col.link && typeof col.link === 'string' && col.link.startsWith('/')) return col.link
  return null
}

function isSelected (id) {
  return props.selectedIds.map(String).includes(String(id))
}

function rowActionsOf (item) {
  const raw = rawOf(item)
  return Array.isArray(raw._rowActions) ? raw._rowActions : []
}

function actionToneClass (tone) {
  return tone ? `lms-index-table__action--${tone}` : ''
}

function isPrimaryCol (col) {
  const key = col?.key || ''
  if (/^(company|name|lessee|applicant|title)$/i.test(key)) return true
  const firstText = props.visibleColumns.find((c) => c.key !== 'id')
  return firstText && firstText.key === col.key
}

function isWrappedCol (col) {
  return String(col?.key || '').toLowerCase() === 'name' ||
    String(col?.title || '').toLowerCase() === 'name' ||
    String(col?.tdClass || '').includes('wrapped')
}

function onClickRow (_event, row) {
  const item = rawOf(row?.item)
  if (!item || item.id == null) return
  if (_event?.altKey) {
    emit('peek', item)
    return
  }
  emit('open-row', item)
  const href = props.rowHref(item)
  if (href) window.location.href = href
}

function csvEscape (val) {
  const s = String(val ?? '')
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`
  return s
}

function exportRows () {
  return props.rows || []
}

async function resolveExportRows () {
  if (typeof props.fetchExportRows === 'function') {
    try {
      const all = await props.fetchExportRows()
      if (Array.isArray(all)) return all
    } catch (_) { /* fall back to page rows */ }
  }
  return exportRows()
}

/** Excel / Print / PDF use the current page only (classic DataTables parity). */
function pageExportRows () {
  return exportRows()
}

const exportColumns = computed(() =>
  (props.visibleColumns || []).filter((column) => column.type !== 'color' && column.key !== 'color')
)

function buildDelimitedFrom (rows, separator = '\t', omitColorValues = false) {
  const cols = exportColumns.value
  const header = cols.map((c) => c.title).join(separator)
  const lines = rows.map((row) =>
    cols.map((c) => {
      const cell = omitColorValues && c.type === 'color' ? '' : displayCell(row, c)
      if (separator === '\t') return String(cell ?? '').replace(/\t/g, ' ')
      return csvEscape(cell)
    }).join(separator)
  )
  return [header, ...lines].join('\n')
}

function buildDelimited (separator = '\t') {
  return buildDelimitedFrom(exportRows(), separator)
}

async function copyRows () {
  const rows = pageExportRows()
  const text = buildDelimitedFrom(rows, '\t', true)
  let copied = false
  try {
    if (navigator.clipboard?.writeText && window.isSecureContext) {
      await navigator.clipboard.writeText(text)
      copied = true
    }
  } catch (_) { /* fall through */ }

  if (!copied) {
    const ta = document.createElement('textarea')
    ta.value = text
    ta.setAttribute('readonly', '')
    ta.style.position = 'fixed'
    ta.style.top = '0'
    ta.style.left = '0'
    ta.style.opacity = '0'
    document.body.appendChild(ta)
    ta.focus()
    ta.select()
    try {
      copied = document.execCommand('copy')
    } catch (_) {
      copied = false
    }
    ta.remove()
  }

  if (copied) {
    showCopyFeedback(rows.length)
    return
  }

  // HTTP / blocked clipboard — download TSV so the action still works.
  const blob = new Blob([text], { type: 'text/tab-separated-values;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `${props.pluralTitle.replace(/\s+/g, '_').toLowerCase()}.tsv`
  a.click()
  URL.revokeObjectURL(url)
  window.alert('Clipboard is unavailable here. Downloaded a TSV file instead.')
}

async function downloadExcel () {
  await exportXlsx({
    title: props.pluralTitle,
    headers: exportColumns.value,
    rows: pageExportRows(),
    filename: `${props.pluralTitle.replace(/\s+/g, '_').toLowerCase()}.xlsx`,
    getCell: (row, key, col) => displayCell(row, col || { key })
  })
}

async function buildPrintHtml () {
  const cols = exportColumns.value
  const rows = pageExportRows()
  const logoUrl = document.querySelector('.navbar-brand__logo')?.currentSrc ||
    document.querySelector('.navbar-brand__logo')?.src ||
    ''
  const logo = logoUrl
    ? `<img class="report-brand__logo" src="${escapeHtml(logoUrl)}" alt="ProDIGIQ">`
    : '<div class="report-brand__wordmark">Pro<span>DIGIQ</span></div>'
  const th = cols.map((c) => {
    const numeric = c.type === 'money' || c.type === 'number'
    return `<th class="${numeric ? 'is-numeric' : ''}">${escapeHtml(c.title)}</th>`
  }).join('')
  const body = rows.map((row) => {
    const tds = cols.map((c) => {
      const numeric = c.type === 'money' || c.type === 'number'
      return `<td class="${numeric ? 'is-numeric' : ''}">${escapeHtml(displayCell(row, c))}</td>`
    }).join('')
    return `<tr>${tds}</tr>`
  }).join('')
  const noun = rows.length === 1 ? 'record' : 'records'
  const generatedAt = new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short'
  }).format(new Date())
  return `<!doctype html><html><head><title>${escapeHtml(props.pluralTitle)}</title>
    <style>
      @page { size: Letter; margin: 0.55in; }
      * { box-sizing: border-box; }
      body { margin: 0; font-family: "Source Sans 3", "Segoe UI", Arial, sans-serif; color: #172b36; }
      .report-header { display: flex; align-items: flex-start; justify-content: space-between; gap: 24px; padding-bottom: 14px; border-bottom: 3px solid #f36f21; }
      .report-brand__logo { display: block; width: 152px; max-height: 48px; object-fit: contain; object-position: left center; }
      .report-brand__wordmark { color: #111820; font-size: 25px; font-weight: 800; letter-spacing: -0.05em; }
      .report-brand__wordmark span { color: #f36f21; }
      .report-brand__sub { margin-top: 3px; color: #536a75; font-size: 10px; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; }
      .report-kicker { color: #0b7ea4; font-size: 10px; font-weight: 700; letter-spacing: 0.12em; text-align: right; text-transform: uppercase; }
      .report-title { margin: 2px 0 3px; color: #123f53; font-size: 22px; line-height: 1.15; text-align: right; }
      .report-meta { color: #687d87; font-size: 10px; text-align: right; }
      table { width: 100%; margin-top: 22px; border: 1px solid #c9d8df; border-collapse: separate; border-spacing: 0; font-size: 11px; }
      thead { display: table-header-group; }
      th, td { padding: 8px 10px; border-bottom: 1px solid #dce6eb; text-align: left; vertical-align: top; }
      th { background: #0b607e; color: #fff; font-size: 10px; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase; }
      th.is-numeric, td.is-numeric { text-align: right; }
      tbody tr:nth-child(even) td { background: #f1f7f9; }
      tbody tr:last-child td { border-bottom: 0; }
      .report-footer { margin-top: 16px; padding-top: 8px; border-top: 1px solid #dce6eb; color: #71858e; font-size: 9px; display: flex; justify-content: space-between; }
      @media print { .report-footer { position: fixed; right: 0; bottom: 0; left: 0; } }
    </style></head><body>
    <header class="report-header">
      <div>${logo}<div class="report-brand__sub">Powered by ATHENA AI™</div></div>
      <div>
        <div class="report-kicker">Lease Management System</div>
        <h1 class="report-title">${escapeHtml(props.pluralTitle)}</h1>
        <div class="report-meta">${rows.length} ${noun} &nbsp;•&nbsp; Generated ${escapeHtml(generatedAt)}</div>
      </div>
    </header>
    <table><thead><tr>${th}</tr></thead><tbody>${body}</tbody></table>
    <footer class="report-footer"><span>ProDIGIQ • Lease Management System</span><span>${escapeHtml(props.pluralTitle)}</span></footer>
    </body></html>`
}

function escapeHtml (s) {
  return String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

function plainText (value) {
  return String(value ?? '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/g, "'")
    .replace(/\s+/g, ' ')
    .trim()
}

async function printInFrame () {
  // Print the generated report in an off-screen frame so Print does not
  // create a second browser window underneath the index grid.
  const frame = document.createElement('iframe')
  frame.setAttribute('aria-hidden', 'true')
  frame.title = 'Print preview'
  Object.assign(frame.style, {
    position: 'fixed',
    width: '1px',
    height: '1px',
    right: '0',
    bottom: '0',
    border: '0',
    opacity: '0',
    pointerEvents: 'none'
  })
  document.body.appendChild(frame)

  let cleanupTimer
  const cleanup = () => {
    clearTimeout(cleanupTimer)
    frame.remove()
  }
  try {
    const html = await buildPrintHtml()
    const doc = frame.contentDocument || frame.contentWindow?.document
    if (!doc) throw new Error('Could not create print document')
    doc.open()
    doc.write(html)
    doc.close()

    frame.contentWindow?.addEventListener('afterprint', cleanup, { once: true })
    cleanupTimer = setTimeout(cleanup, 60000)
    setTimeout(() => {
      try { frame.contentWindow?.print() } catch (_) { cleanup() }
    }, 150)
  } catch (e) {
    cleanup()
    window.alert(`Could not build print: ${escapeHtml(e?.message || e)}`)
  }
}

function pdfDocument (rows) {
  const cols = exportColumns.value
  const wide = cols.length > 4
  const noun = rows.length === 1 ? 'record' : 'records'
  const generatedAt = new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short'
  }).format(new Date())
  const body = [
    cols.map((col) => ({
      text: plainText(col.title).toUpperCase(),
      style: 'tableHeader',
      alignment: col.type === 'money' || col.type === 'number' ? 'right' : 'left',
      noWrap: false
    })),
    ...rows.map((row) =>
      cols.map((col) => ({
        text: plainText(displayCell(row, col)),
        style: 'tableBody',
        alignment: col.type === 'money' || col.type === 'number' ? 'right' : 'left',
        noWrap: false
      }))
    )
  ]

  return {
    pageSize: 'LETTER',
    pageOrientation: wide ? 'landscape' : 'portrait',
    pageMargins: wide ? [24, 34, 24, 38] : [36, 42, 36, 44],
    content: [
      {
        columns: [
          {
            width: '*',
            stack: [
              { text: 'ProDIGIQ', style: 'brand' },
              { text: 'POWERED BY ATHENA AI™', style: 'brandSub' }
            ]
          },
          {
            width: 'auto',
            stack: [
              { text: 'LEASE MANAGEMENT SYSTEM', style: 'kicker', alignment: 'right' },
              { text: props.pluralTitle || 'Export', style: 'title', alignment: 'right' },
              { text: `${rows.length} ${noun}  •  Generated ${generatedAt}`, style: 'meta', alignment: 'right' }
            ]
          }
        ],
        margin: [0, 0, 0, 10]
      },
      {
        canvas: [{ type: 'line', x1: 0, y1: 0, x2: wide ? 744 : 540, y2: 0, lineWidth: 2, lineColor: '#f36f21' }],
        margin: [0, 0, 0, 16]
      },
      {
        table: {
          headerRows: 1,
          dontBreakRows: true,
          widths: cols.map(() => '*'),
          body
        },
        layout: {
          fillColor: (rowIndex) => rowIndex === 0 ? '#0B607E' : (rowIndex % 2 === 0 ? '#f1f7f9' : null),
          hLineWidth: () => 0.5,
          vLineWidth: () => 0.5,
          hLineColor: () => '#c9d8df',
          vLineColor: () => '#c9d8df',
          paddingLeft: () => 7,
          paddingRight: () => 7,
          paddingTop: () => 5,
          paddingBottom: () => 5
        }
      }
    ],
    footer: (currentPage, pageCount) => ({
      columns: [
        { text: 'ProDIGIQ • Lease Management System', alignment: 'left' },
        { text: `Page ${currentPage} of ${pageCount}`, alignment: 'right' }
      ],
      margin: [36, 12, 36, 0],
      style: 'footer'
    }),
    styles: {
      brand: { fontSize: 19, bold: true, color: '#111820', characterSpacing: -0.6 },
      brandSub: { fontSize: 7, bold: true, color: '#536a75', characterSpacing: 0.7, margin: [0, 2, 0, 0] },
      kicker: { fontSize: 7, bold: true, color: '#0B7EA4', characterSpacing: 1.1 },
      title: { fontSize: 17, bold: true, color: '#123F53', margin: [0, 2, 0, 2] },
      meta: { fontSize: 8, color: '#687D87' },
      tableHeader: { bold: true, fontSize: wide ? 7 : 8, color: '#ffffff' },
      tableBody: { fontSize: wide ? 7 : 9, color: '#172b36' },
      footer: { fontSize: 7, color: '#71858e' }
    },
    defaultStyle: { fontSize: wide ? 7 : 9 }
  }
}

async function downloadPdf () {
  const pdfMake = window.pdfMake
  if (!pdfMake?.createPdf || !pdfMake.vfs) {
    // Keep a usable fallback without triggering a second print-preview window.
    await printInFrame()
    return
  }

  const rows = pageExportRows()
  const filename = `${props.pluralTitle.replace(/\s+/g, '_').toLowerCase()}.pdf`
  try {
    pdfMake.createPdf(pdfDocument(rows)).download(filename)
  } catch (e) {
    console.error('LMS PDF export failed; falling back to print', e)
    await printInFrame()
  }
}

async function printRows () {
  await printInFrame()
}

defineExpose({
  copyRows,
  downloadExcel,
  downloadPdf,
  printRows
})
</script>

<style scoped>
.lms-index-table.is-embedded .lms-index-table__wrap {
  border: 0;
  border-radius: 0;
  border-top: 1px solid var(--lms-border);
}

.lms-index-table__wrap {
  border: 1px solid var(--lms-border);
  border-radius: 12px;
  overflow-x: auto;
  overflow-y: hidden;
  background: var(--lms-surface);
  -webkit-overflow-scrolling: touch;
}

.lms-index-table__dt :deep(table) {
  font-family: 'Source Sans 3', sans-serif;
  table-layout: auto;
  min-width: 720px;
  width: 100%;
}

.lms-index-table__dt :deep(thead th),
.lms-index-table__dt :deep(tbody td) {
  word-break: normal !important;
  overflow-wrap: normal !important;
}

.lms-index-table__dt :deep(thead th) {
  white-space: nowrap !important;
  min-width: 110px;
}

.lms-index-table__dt :deep(tbody td) {
  white-space: nowrap;
}

.lms-index-table__dt :deep(tbody td:first-child),
.lms-index-table__dt :deep(thead th:first-child) {
  min-width: 72px;
}

.lms-index-table__dt :deep(tbody td:last-child),
.lms-index-table__dt :deep(thead th:last-child) {
  min-width: 168px;
}

.lms-index-table__dt :deep(thead th) {
  font-size: 0.875rem !important;
  font-weight: 700 !important;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--lms-text-secondary) !important;
  background: var(--app-control, #f8fafc) !important;
  border-bottom: 1px solid var(--lms-border) !important;
  white-space: nowrap;
}

.lms-index-table__dt :deep(thead th .v-data-table-header__content) {
  font-size: inherit;
}

.lms-index-table__dt :deep(thead th.v-data-table-column--align-end .v-data-table-header__content),
.lms-index-table__dt :deep(thead th[style*='text-align: end'] .v-data-table-header__content),
.lms-index-table__dt :deep(thead th[style*='text-align:end'] .v-data-table-header__content) {
  width: 100%;
  justify-content: flex-end;
}

.lms-index-table__dt :deep(tbody td.v-data-table-column--align-end) {
  text-align: end !important;
}

.lms-index-table__dt :deep(thead th:first-child),
.lms-index-table__dt :deep(tbody td:first-child) {
  width: 1%;
  white-space: nowrap;
  padding-left: 22px !important;
  padding-right: 10px !important;
  text-align: left !important;
}

.lms-index-table__dt :deep(tbody td:last-child) {
  padding-right: 18px !important;
}

.lms-index-table__dt :deep(tbody tr) {
  cursor: pointer;
  transition: background 140ms ease;
}

.lms-index-table__dt :deep(tbody tr:hover) {
  background: rgba(11, 126, 164, 0.045) !important;
}

.lms-index-table__dt :deep(tbody td) {
  font-size: 0.875rem;
  color: var(--lms-text);
  background: var(--lms-surface) !important;
  border-bottom: 1px solid var(--lms-border) !important;
  padding-top: 14px !important;
  padding-bottom: 14px !important;
}

.lms-index-table__cell {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
  min-width: 0;
}

.lms-index-table__primary {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  min-width: 0;
  min-width: 180px;
}

.lms-index-table__title-link {
  color: var(--lms-primary);
  font-weight: 700;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 280px;
}

.lms-index-table__title-link--wrapped {
  width: 280px;
  max-width: min(280px, calc(100vw - 80px));
  white-space: normal !important;
  overflow: visible;
  text-overflow: clip;
  overflow-wrap: normal;
  word-break: normal;
  line-height: 1.35;
}

.lms-index-table__signals {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.lms-index-table__signal {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  border-radius: 999px;
  padding: 2px 8px 2px 6px;
  line-height: 1.3;
  white-space: nowrap;
}

.lms-index-table__signal-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
}

.lms-index-table__signal.is-danger {
  color: #9f1239;
  background: rgba(225, 29, 72, 0.12);
}

.lms-index-table__signal.is-warning {
  color: #9a3412;
  background: rgba(249, 115, 22, 0.14);
}

.lms-index-table__signal.is-info {
  color: #0a4f6c;
  background: rgba(11, 126, 164, 0.12);
}

.lms-index-table__signal.is-neutral {
  color: #475569;
  background: rgba(100, 116, 139, 0.12);
}

.lms-index-table__actions {
  display: inline-flex;
  gap: 6px;
  justify-content: flex-start;
  width: 100%;
  white-space: nowrap;
}

.lms-index-table__action {
  appearance: none;
  border: 0;
  border-radius: 6px;
  padding: 6px 12px;
  font-size: 0.75rem;
  font-weight: 500;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  text-decoration: none;
  cursor: pointer;
  line-height: 1;
  white-space: nowrap;
}

.lms-index-table__action--edit {
  background: rgba(46, 125, 50, 0.12);
  color: #1b5e20;
}

.lms-index-table__action--edit:hover {
  background: rgba(46, 125, 50, 0.2);
}

.lms-index-table__action--peek {
  background: rgba(11, 126, 164, 0.12);
  color: #0a4f6c;
}

.lms-index-table__action--peek:hover {
  background: rgba(11, 126, 164, 0.2);
}

.lms-index-table__action--success {
  border: 1px solid #a9d9c2 !important;
  background: #e6f4ee;
  color: #176b4d;
  font-weight: 700;
}

.lms-index-table__action--success:hover {
  background: #cfeadd;
}

.lms-index-table__clamp {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
  overflow: hidden;
}

.lms-index-table__swatch {
  display: inline-block;
  width: 220px;
  height: 18px;
  border-radius: 3px;
  border: 1px solid var(--lms-border);
  vertical-align: middle;
}

.lms-index-table__link-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: var(--lms-primary);
  font-weight: 600;
  text-decoration: none;
}

.lms-index-table__html :deep(a) {
  color: var(--lms-primary);
  font-weight: 600;
}

.lms-index-table__html,
.lms-index-table__clamp {
  white-space: normal;
  overflow-wrap: anywhere;
}

.lms-index-table__action--danger {
  color: #b42318;
}

.lms-index-table__action {
  border: 0;
  cursor: pointer;
}

.lms-index-table__loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 32px;
  color: var(--lms-text-secondary);
}

.lms-index-table__footer {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px 16px;
  width: 100%;
  padding: 8px 12px 12px;
}

.lms-index-table__footer-meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px 16px;
  min-width: 0;
}

.lms-index-table__footer-summary {
  color: rgba(var(--v-theme-on-surface), 0.6);
  font-size: 0.875rem;
  line-height: 1.2;
  white-space: nowrap;
}
</style>
